import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Paperclip, X, ImageSquare, FilmSlate, FileText, MusicNote, ArrowsClockwise, Plus } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_GROUPS = {
  image:    ['.jpg', '.jpeg', '.png', '.webp'],
  video:    ['.mp4', '.mov', '.webm'],
  document: ['.pdf', '.txt', '.docx'],
  audio:    ['.mp3', '.wav', '.m4a'],
};

const iconForType = (t) => {
  if (t === 'image') return ImageSquare;
  if (t === 'video') return FilmSlate;
  if (t === 'audio') return MusicNote;
  return FileText;
};

/**
 * ReferenceMediaUpload — single, reusable optional-media uploader.
 * Used identically across every content-creation tool under the Zero
 * Friction mandate. Collapsed by default for most tools.
 *
 * Props
 *  - label          : button copy when collapsed
 *  - note           : per-tool contextual hint shown inside the expanded area
 *  - acceptedTypes  : array of file extension groups, e.g. ['image','video']
 *  - maxFiles       : default 3
 *  - maxSizeMB      : default 25
 *  - isRequired     : default false. true forces expanded state.
 *  - onChange       : callback(files) — files = [{url, file_id, type, filename, size}]
 *  - initialFiles   : optional starting files
 */
const ReferenceMediaUpload = ({
  label = 'Add reference media (optional)',
  note = 'Photos, videos, or files to guide the output',
  acceptedTypes = ['image', 'video', 'document'],
  maxFiles = 3,
  maxSizeMB = 25,
  isRequired = false,
  onChange,
  initialFiles = [],
}) => {
  const [expanded, setExpanded] = useState(isRequired);
  const [files, setFiles] = useState(initialFiles);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');
  const inputRef = useRef(null);

  const acceptExts = acceptedTypes.flatMap((t) => TYPE_GROUPS[t] || []);
  const acceptStr = acceptExts.join(',');

  const emit = (next) => {
    setFiles(next);
    if (onChange) onChange(next);
  };

  const handleUpload = async (e) => {
    setErr('');
    const incoming = Array.from(e.target.files || []);
    if (!incoming.length) return;
    if (files.length + incoming.length > maxFiles) {
      setErr(`Up to ${maxFiles} files`);
      return;
    }
    for (const f of incoming) {
      const ext = '.' + (f.name.split('.').pop() || '').toLowerCase();
      if (!acceptExts.includes(ext)) {
        setErr(`File type ${ext} not supported`);
        continue;
      }
      if (f.size > maxSizeMB * 1024 * 1024) {
        setErr(`${f.name} is larger than ${maxSizeMB}MB`);
        continue;
      }
      setUploading(true);
      try {
        const fd = new FormData();
        fd.append('file', f);
        const { data } = await axios.post(`${API}/api/uploads/file`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const newFile = {
          file_id: data.file_id,
          url: data.url,
          type: data.file_type,
          filename: data.original_name || f.name,
          size: data.size,
        };
        emit([...(files || []), newFile]);
      } catch (e2) {
        setErr(e2?.response?.data?.detail || 'Upload failed');
      } finally {
        setUploading(false);
      }
    }
    if (inputRef.current) inputRef.current.value = '';
  };

  const removeAt = (idx) => {
    const next = files.filter((_, i) => i !== idx);
    emit(next);
  };

  if (!expanded) {
    return (
      <button onClick={() => setExpanded(true)} data-testid="ref-media-expand"
        style={{
          width: '100%', padding: '10px 14px', borderRadius: 12,
          background: 'rgba(255,255,255,0.02)', border: '1px dashed rgba(255,255,255,0.1)',
          color: 'rgba(245,240,232,0.45)', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'flex-start',
          fontFamily: 'ui-monospace, monospace', fontSize: 11, letterSpacing: '0.04em',
        }}>
        <Plus size={14} weight="bold" />
        <span>{label}</span>
      </button>
    );
  }

  return (
    <div data-testid="ref-media-panel" style={{
      borderRadius: 12, padding: 14,
      background: 'rgba(255,255,255,0.02)',
      border: '1px solid rgba(255,255,255,0.06)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <p style={{ fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: 13, color: '#f5f0e8' }}>
          {isRequired ? label.replace(/\s*\(optional\)\s*$/, '') : label}
        </p>
        {!isRequired && (
          <button onClick={() => { setExpanded(false); setErr(''); }} data-testid="ref-media-collapse"
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'rgba(245,240,232,0.35)' }}>
            <X size={14} />
          </button>
        )}
      </div>
      <p style={{ fontSize: 11, color: 'rgba(245,240,232,0.4)', marginBottom: 10, lineHeight: 1.5 }}>{note}</p>

      <input ref={inputRef} type="file" accept={acceptStr} multiple={maxFiles > 1}
        onChange={handleUpload} style={{ display: 'none' }} data-testid="ref-media-input" />

      <button onClick={() => inputRef.current?.click()} disabled={uploading || files.length >= maxFiles}
        data-testid="ref-media-upload-btn"
        style={{
          width: '100%', padding: '14px', borderRadius: 10,
          background: 'rgba(200,240,96,0.05)', border: '1px dashed rgba(200,240,96,0.25)',
          color: '#c8f060', cursor: uploading || files.length >= maxFiles ? 'not-allowed' : 'pointer',
          display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center',
          fontFamily: 'ui-monospace, monospace', fontSize: 11.5, letterSpacing: '0.04em',
          opacity: uploading || files.length >= maxFiles ? 0.5 : 1,
        }}>
        {uploading ? (
          <><ArrowsClockwise size={14} className="animate-spin" /> Uploading…</>
        ) : (
          <><Paperclip size={14} /> {files.length === 0 ? 'Tap to upload' : `Add another (${files.length}/${maxFiles})`}</>
        )}
      </button>

      {err && <p style={{ marginTop: 8, fontSize: 11, color: '#ff6b6b' }}>{err}</p>}

      {files.length > 0 && (
        <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {files.map((f, i) => {
            const Icon = iconForType(f.type);
            return (
              <div key={f.file_id || i} data-testid={`ref-media-chip-${i}`}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '5px 10px 5px 8px', borderRadius: 999,
                  background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)',
                }}>
                <Icon size={12} weight="fill" style={{ color: '#c8f060' }} />
                <span style={{ fontSize: 11, color: 'rgba(245,240,232,0.75)', maxWidth: 160,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {f.filename}
                </span>
                <button onClick={() => removeAt(i)} data-testid={`ref-media-chip-${i}-remove`}
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer',
                    color: 'rgba(255,107,107,0.6)', padding: 0, display: 'inline-flex' }}>
                  <X size={11} weight="bold" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ReferenceMediaUpload;
